import type { TaskRun, TaskRunStateProjection } from "../../../shared/types";

type RunOutcomeInput = Pick<TaskRun, "task_slug" | "status" | "readiness" | "seo_research" | "collection"> & {
  state_projection?: (Pick<TaskRunStateProjection, "execution_outcome" | "no_op_reason"> & { label?: string }) | null;
  output?: Record<string, unknown> | null;
};

export type RunOutcomeState = "working" | "ready" | "needs_attention" | "failed" | "skipped" | "retried" | "completed";

export type RunOutcome = {
  state: RunOutcomeState;
  label: string;
  description: string;
  actionLabel: "Review Result" | "Open Result";
};

function isCurrentEventsResearch(run: RunOutcomeInput): boolean {
  return run.task_slug === "current_events_campaign_research";
}

function outcomeString(run: RunOutcomeInput, key: "execution_outcome" | "needs_attention_reason"): string | null {
  const value = run.output?.[key];
  if (typeof value === "string" && value.trim()) return value.trim();

  // Activity receives the compact state projection, not detail-only task output.
  // `no_op_reason` intentionally carries the allow-listed attention reason.
  if (key === "execution_outcome") return run.state_projection?.execution_outcome ?? null;
  return run.state_projection?.no_op_reason ?? null;
}

/**
 * Converts backend execution facts into the one state a marketer needs to act on.
 * `hasUsableReport` is optional because Activity only has the run projection; Run
 * Detail supplies it after it resolves the expected work product.
 */
export function runOutcome(run: RunOutcomeInput, hasUsableReport?: boolean): RunOutcome {
  const seo = run.seo_research;
  if (seo) {
    if (seo.status === "review_required") {
      const counts = seo.review
        ? ` ${seo.review.acceptedCount} of ${seo.review.termCount} keyword assessments accepted; ${seo.review.researchMoreCount} needs more research.`
        : "";
      return {
        state: "needs_attention", label: "Review required", actionLabel: "Review Result",
        description: `Some keyword assessments need more evidence before use. Review the saved research report.${counts}`,
      };
    }
    if (seo.status === "held") return {
      state: "needs_attention", label: "Held", actionLabel: "Open Result",
      description: seo.reason === "invalid_evidence"
        ? "Research evidence could not be validated. An authorized operator must review this Run."
        : "Research evidence is unavailable or awaiting confirmation. Reload to check this same Run.",
    };
    if (seo.status === "completed") return {
      state: "ready", label: "Completed", description: "Verified keyword research is ready to review.", actionLabel: "Review Result",
    };
    if (seo.status === "failed") return {
      state: "failed", label: "Failed", description: "SEO research execution failed.", actionLabel: "Open Result",
    };
    return { state: "working", label: "In progress", description: "SEO research is awaiting completion.", actionLabel: "Open Result" };
  }
  if (run.status === "running" || run.status === "retrying") {
    return { state: "working", label: "In progress", description: "The agent is working on this request.", actionLabel: "Open Result" };
  }
  if (run.status === "failed") {
    return { state: "failed", label: "Failed", description: "This work did not finish and needs support before it can be retried.", actionLabel: "Open Result" };
  }
  if (run.status === "skipped") {
    if (!isCurrentEventsResearch(run)) {
      return {
        state: "skipped",
        label: "Skipped",
        description: "This work was skipped.",
        actionLabel: "Open Result",
      };
    }
    return {
      state: "skipped",
      label: "Needs attention",
      description: "This research did not start. Review the question and rerun when you are ready.",
      actionLabel: "Open Result",
    };
  }
  if (run.status === "retried") {
    return { state: "retried", label: "Superseded by retry", description: "A retry replaced this run.", actionLabel: "Open Result" };
  }

  if (run.task_slug.startsWith("dataset_query_sync:")) {
    const dataset = run.collection?.dataset;
    if (dataset && run.collection?.state === "collected") return { state: "ready", label: "Collected", description: `${dataset.row_count} saved rows are readable. Inspect or download this Run's snapshot.`, actionLabel: "Open Result" };
    if (dataset && run.collection?.state === "partial") return { state: "needs_attention", label: "Partial collection", description: `${dataset.row_count} partial saved rows are readable. Inspect coverage before use.`, actionLabel: "Open Result" };
    return { state: "needs_attention", label: run.state_projection?.label ?? "Evidence unavailable", description: "Saved dataset evidence was not verified in this view. Inspect this Run's selected snapshot.", actionLabel: "Open Result" };
  }

  const noDatedSources = outcomeString(run, "needs_attention_reason") === "no_dated_sources_in_window";
  const needsAttention = outcomeString(run, "execution_outcome") === "needs_attention";
  if (isCurrentEventsResearch(run) && (needsAttention || hasUsableReport === false)) {
    return {
      state: "needs_attention",
      label: "Needs attention",
      description: noDatedSources
        ? "No dated sources met this research window, so no campaign direction was generated."
        : "This research did not produce a readable report. Review the question and rerun when appropriate.",
      actionLabel: "Open Result",
    };
  }

  if (run.readiness === "usable" || run.readiness === "usable_with_warnings") {
    return { state: "ready", label: "Ready to review", description: "A result is ready to review.", actionLabel: "Review Result" };
  }
  return { state: "completed", label: "Completed", description: "Work completed.", actionLabel: "Open Result" };
}

export function runOutcomeBadgeVariant(state: RunOutcomeState): "success" | "warning" | "error" | "default" {
  if (state === "ready") return "success";
  if (state === "needs_attention" || state === "skipped") return "warning";
  if (state === "failed") return "error";
  return "default";
}
